import { css } from '@emotion/react';
import {
  type FC, useEffect, useMemo, useState,
} from 'react';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { type SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { useIsNumericColumnCheck } from '~/heatMap/panel/form/useIsNumericColumnCheck.hook';
import { useActiveBoundaryFillEdit } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/useActiveBoundaryFillEdit';
import { BoundarySelectedOption } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.enums';
import {
  BoundaryTerritoryType, type CalculateBucketFunctionNumeric,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';

type CustomLabelConfigurationProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  setIsFormValid: (isFormValid: boolean) => void;
  setIsLoading: (isFormValid: boolean) => void;
}>;

// Copied styles and options from ActiveBoundaryFillEditComponent
const panelDropdownSectionStyle = css({
});

const labelStyle = css({
  fontSize: 14,
  marginBottom: 4,
  textTransform: 'uppercase',
});

const dropdownPlaceholderStyle = (theme: Theme) => css({
  color: theme.textColors.disabled,
});

const dropdownTriggerStyle = css({
  height: 40,
});

const hiddenBoundaryTerritoryTypeOptions: ReadonlyArray<BoundaryTerritoryType> = [
  BoundaryTerritoryType.Manual,
];

const getCalculateBucketFunctionOptions = (
  t: TranslationFnc
): ReadonlyArray<DropdownOption<CalculateBucketFunctionNumeric>> => ([{
  name: t('Sum'),
  value: 'sum',
}, {
  name: t('Average'),
  value: 'avg',
}, {
  name: t('Min'),
  value: 'min',
}, {
  name: t('Max'),
  value: 'max',
}]);

export const CustomLabelConfigurationComponent: FC<CustomLabelConfigurationProps> = ({
  boundaryTerritoryGroup,
  onBoundaryTerritoryGroupChange,
  setIsFormValid,
  setIsLoading,
}) => {
  const { labelValueConfig } = boundaryTerritoryGroup.settings;
  const { checkIsNumeric, isLoading: isCheckNumericLoading } = useIsNumericColumnCheck();
  const spreadsheetId = usePrimarySpreadsheetId();
  const [isValidNumericalColumn, setIsValidNumericalColumn] = useState(true);
  const [t] = useTranslation();

  const spreadsheetColumns = useSpreadsheetColumns();
  const initialBoundaryFillData: SpreadsheetColumnId | null = useMemo(() => {
    const columnId = labelValueConfig.labelValueColumnId;
    if (!columnId) {
      return null;
    }

    const column = spreadsheetColumns.find(col => col.id === columnId);
    return column ? { spreadsheetId: column.spreadsheetId, columnId: column.id } : null;
  }, [labelValueConfig.labelValueColumnId, spreadsheetColumns]);

  const initialNumericCalculateBucketFunction: CalculateBucketFunctionNumeric | null = useMemo(() => {
    const fn = labelValueConfig.labelValueAggregationFunction;
    return (fn === 'min' || fn === 'avg' || fn === 'max' || fn === 'sum') ? fn : null;
  }, [labelValueConfig.labelValueAggregationFunction]);

  const {
    hideFillData,
    showDemographicGroup,
    boundaryTerritoryTypeOptions,
    selectedDemographicCategory,
    onSelectedDemographicCategoryChange,
    demographicCategoryOptions,
    spreadsheetColumnOptions,
    demographicOptions,
    boundaryFillModel: {
      boundaryTerritoryType,
      boundaryFillData,
      selectedDemographicId,
      numericCalculateBucketFunction,
    },
    isUpdateDisabled,
    onBoundaryFillDataChange: setBoundaryFillData,
    onBoundaryTerritoryTypeChange: setBoundaryTerritoryType,
    onDemographicIdChange: setSelectedDemographicId,
    onNumericCalculateBucketFunctionChange: setNumericCalculateBucketFunction,
  } = useActiveBoundaryFillEdit({
    boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
    initialBoundaryTerritoryType: labelValueConfig.labelValueType,
    initialBoundaryFillData,
    initialDemographicId: labelValueConfig.labelValueDemographicId,
    initialNumericCalculateBucketFunction,
    hiddenBoundaryTerritoryTypeOptions,
  });

  useEffect(() => {
    setIsFormValid(!isCheckNumericLoading && isValidNumericalColumn && !isUpdateDisabled);
  }, [isCheckNumericLoading, isUpdateDisabled, isValidNumericalColumn, setIsFormValid]);

  useEffect(() => {
    setIsLoading(isCheckNumericLoading);
  }, [isCheckNumericLoading, setIsLoading]);

  useEffect(() => {
    if (labelValueConfig.labelValueType === BoundaryTerritoryType.Numeric) {
      if (labelValueConfig.labelValueColumnId) {
        checkIsNumeric(labelValueConfig.labelValueColumnId, spreadsheetId)
          .then((isNumerical) => setIsValidNumericalColumn(isNumerical));
      }
      else {
        setIsValidNumericalColumn(false);
      }
    }
    else {
      setIsValidNumericalColumn(true);
    }
  }, [boundaryTerritoryType, checkIsNumeric, labelValueConfig.isValueCustom, labelValueConfig.labelValueColumnId, labelValueConfig.labelValueType, spreadsheetId]);

  // Helpers to update BoundaryTerritoryGroup label value config
  const updateLabelConfig = (update: (prev: typeof labelValueConfig) => typeof labelValueConfig) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        labelValueConfig: update(boundaryTerritoryGroup.settings.labelValueConfig),
      },
    });
  };

  const handleBoundaryTerritoryTypeChange = (type: BoundaryTerritoryType) => {
    setBoundaryTerritoryType(type);
    updateLabelConfig(prev => {
      // Reset unrelated fields depending on the selected type
      switch (type) {
        case BoundaryTerritoryType.Demographic: {
          return {
            ...prev,
            labelValueType: type,
            labelValueDemographicId: prev.labelValueDemographicId,
            labelValueColumnId: null,
            labelValueAggregationFunction: null,
          };
        }
        case BoundaryTerritoryType.Numeric: {
          return {
            ...prev,
            labelValueType: type,
            labelValueColumnId: prev.labelValueColumnId,
            labelValueAggregationFunction: prev.labelValueAggregationFunction,
            labelValueDemographicId: null,
          };
        }
        case BoundaryTerritoryType.Groups: {
          return {
            ...prev,
            labelValueType: type,
            labelValueColumnId: prev.labelValueColumnId,
            labelValueAggregationFunction: 'equal',
            labelValueDemographicId: null,
          };
        }
        default: // Density
          return {
            ...prev,
            labelValueType: type,
            labelValueColumnId: null,
            labelValueAggregationFunction: null,
            labelValueDemographicId: null,
          };
      }
    });
  };

  const handleDemographicIdChange = (id: string) => {
    setSelectedDemographicId(id);
    updateLabelConfig(prev => ({
      ...prev,
      labelValueDemographicId: id,
    }));
  };

  const handleBoundaryFillDataChange = (fillData: SpreadsheetColumnId) => {
    setBoundaryFillData(fillData);
    updateLabelConfig(prev => ({
      ...prev,
      labelValueColumnId: fillData.columnId,
    }));
  };

  const handleNumericCalculateBucketFunctionChange = (fn: CalculateBucketFunctionNumeric) => {
    setNumericCalculateBucketFunction(fn);
    updateLabelConfig(prev => ({
      ...prev,
      labelValueAggregationFunction: fn,
    }));
  };

  return (
    <>
      <div css={panelDropdownSectionStyle}>
        <div css={labelStyle}>
          {t('Select Value Source')}
        </div>

        <RegularDropdownComponent
          onChange={handleBoundaryTerritoryTypeChange}
          options={boundaryTerritoryTypeOptions}
          placeholder={t(BoundarySelectedOption.BoundaryTerritoryType)}
          triggerStyle={[dropdownTriggerStyle, boundaryTerritoryType ? undefined : dropdownPlaceholderStyle]}
          value={boundaryTerritoryType}
        />
      </div>

      {boundaryTerritoryType && showDemographicGroup && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {t(BoundarySelectedOption.DemographicGroup)}
          </div>
          <RegularDropdownComponent
            onChange={onSelectedDemographicCategoryChange}
            options={demographicCategoryOptions}
            placeholder={t(BoundarySelectedOption.DemographicGroup)}
            triggerStyle={[dropdownTriggerStyle, selectedDemographicCategory ? undefined : dropdownPlaceholderStyle]}
            value={selectedDemographicCategory}
          />
        </div>
      )}

      {!hideFillData && showDemographicGroup && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {t(BoundarySelectedOption.BoundaryFillData)}
          </div>
          <RegularDropdownComponent
            onChange={handleDemographicIdChange}
            options={demographicOptions}
            placeholder={t(BoundarySelectedOption.BoundaryFillData)}
            triggerStyle={dropdownTriggerStyle}
            value={selectedDemographicId}
          />
        </div>
      )}

      {!hideFillData && boundaryTerritoryType === BoundaryTerritoryType.Numeric && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {t(BoundarySelectedOption.SelectCalculateFunction)}
          </div>

          <RegularDropdownComponent<CalculateBucketFunctionNumeric>
            onChange={handleNumericCalculateBucketFunctionChange}
            options={getCalculateBucketFunctionOptions(t)}
            placeholder={t(BoundarySelectedOption.SelectCalculateFunction)}
            triggerStyle={[dropdownTriggerStyle, numericCalculateBucketFunction ? undefined : dropdownPlaceholderStyle]}
            value={numericCalculateBucketFunction}
          />
        </div>
      )}

      {!hideFillData && !showDemographicGroup && ((boundaryTerritoryType !== BoundaryTerritoryType.Numeric) || numericCalculateBucketFunction) && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {t(BoundarySelectedOption.BoundaryFillData)}
          </div>

          <RegularDropdownComponent<SpreadsheetColumnId>
            onChange={handleBoundaryFillDataChange}
            options={spreadsheetColumnOptions}
            placeholder={t(BoundarySelectedOption.BoundaryFillData)}
            triggerStyle={dropdownTriggerStyle}
            compareOptions={(a, b) => (a?.columnId === b?.columnId) && (a?.spreadsheetId === b?.spreadsheetId)}
            value={boundaryFillData}
          />
          {!isValidNumericalColumn && !isCheckNumericLoading && labelValueConfig.labelValueColumnId && (
            <FormErrorMessageComponent
              isSingleLine={false}
              messages={[t('Selected data is not numerical')]}
            />
          )}
        </div>
      )}
    </>
  );
};
